import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import { PLATFORM_SERVICE_KEYS, type EnvironmentProbeResult, type EnvironmentView } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, TextField } from '@/components/ops/Forms';
import { PageHeader, Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { useEnvironments } from '@/ops/useEnvironment';

const KEY_PATTERN = /^[a-z][a-z0-9-]{1,31}$/;

/** An absolute http(s) URL with no credentials and no query — the API's own rule (docs §2). */
export function serviceUrlError(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return 'Must be http(s).';
    if (parsed.username || parsed.password) return 'No credentials in the URL.';
    if (parsed.search || parsed.hash) return 'No query string or fragment.';
    return null;
  } catch {
    return 'An absolute URL, e.g. http://billing-api:8080';
  }
}

/** The internal key's own rule: 16–512 characters, no whitespace. */
export function internalKeyError(key: string): string | null {
  if (key.length === 0) return null;
  if (/\s/.test(key)) return 'No whitespace.';
  if (key.length < 16 || key.length > 512) return '16–512 characters.';
  return null;
}

/**
 * The environments registry (D-CP12): each managed stack's service URLs, its write-only internal key
 * (IDataProtector-encrypted — the page only ever sees whether one is set, CP4), and the collector mapping.
 */
export function EnvironmentsPage() {
  const query = useEnvironments();
  const canManage = hasPermission(PERMISSIONS.environmentsManage);
  const [editing, setEditing] = useState<EnvironmentView | 'new' | null>(null);
  const [keyFor, setKeyFor] = useState<EnvironmentView | null>(null);
  const [probe, setProbe] = useState<EnvironmentProbeResult | null>(null);

  const runProbe = useMutation({
    mutationFn: (key: string) => opsApi.environments.probe(key),
    onSuccess: setProbe,
  });

  return (
    <RequirePermission permission={PERMISSIONS.hostsRead} what="The environments registry">
      <PageHeader
        title="Environments"
        subtitle="Managed stacks the console provisions into and reads from. Internal keys are write-only."
        actions={canManage && (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>Add environment</button>
        )}
      />

      <QueryState query={query}>
        {(data) => (
          <Section title="Registry" meta={`${data.environments.length} environment(s)`} flush>
            {data.environments.length === 0 ? (
              <div className="empty-state t-body">No environments yet.</div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Environment</th>
                      <th>State</th>
                      <th>Internal key</th>
                      <th>Services</th>
                      <th>Collector mapping</th>
                      <th>Updated</th>
                      {canManage && <th aria-label="Actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {data.environments.map((env) => (
                      <tr key={env.key}>
                        <td>
                          <Link to={`/ops/environments/${encodeURIComponent(env.key)}`} style={{ fontWeight: 600 }}>
                            {env.label}
                          </Link>
                          <div className="t-caption muted mono">{env.key}</div>
                        </td>
                        <td><StatusChip status={env.enabled ? 'enabled' : 'disabled'} /></td>
                        <td>
                          {env.has_internal_api_key ? (
                            <>
                              <StatusChip status="set" tone="success" />
                              <div className="t-caption muted">{formatTimestamp(env.key_set_at)}</div>
                            </>
                          ) : (
                            <StatusChip status="missing" tone="warning" title="Every call to this environment answers key_missing until a key is set." />
                          )}
                        </td>
                        <td className="t-caption" title={Object.entries(env.services).map(([k, v]) => `${k}: ${v}`).join('\n')}>
                          {Object.keys(env.services).length} mapped
                          <div className="muted">{Object.keys(env.services).slice(0, 6).join(', ')}{Object.keys(env.services).length > 6 ? '…' : ''}</div>
                        </td>
                        <td className="t-caption mono">
                          {env.compose_project ?? <span className="subtle">no compose project</span>}
                          <div className="muted">{env.postgres_container ?? '—'}</div>
                        </td>
                        <td className="t-caption muted">{formatTimestamp(env.updated_at ?? env.created_at)}</td>
                        {canManage && (
                          <td>
                            <div className="row">
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(env)}>Edit</button>
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setKeyFor(env)}>
                                {env.has_internal_api_key ? 'Rotate key' : 'Set key'}
                              </button>
                              <button type="button" className="btn btn-ghost btn-sm" disabled={runProbe.isPending}
                                onClick={() => runProbe.mutate(env.key)}>
                                {runProbe.isPending && runProbe.variables === env.key ? 'Probing…' : 'Probe'}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}
      </QueryState>

      <MutationError error={runProbe.error} />
      {probe && <ProbeResult result={probe} onClose={() => setProbe(null)} />}
      {editing && <EnvironmentForm environment={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {keyFor && <InternalKeyForm environment={keyFor} onClose={() => setKeyFor(null)} />}
    </RequirePermission>
  );
}

function ProbeResult({ result, onClose }: { result: EnvironmentProbeResult; onClose: () => void }) {
  const reachable = result.services.filter((s) => s.reachable).length;
  return (
    <Modal title={`Probe — ${result.environment}`} onClose={onClose} size="lg"
      footer={<button type="button" className="btn btn-primary" onClick={onClose}>Close</button>}>
      <p className="t-caption muted" style={{ margin: 0 }}>
        GET /health/live on every configured service — proves reach from the console's network, not the key.
        {' '}{reachable} of {result.services.length} reachable.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Service</th><th>Reachable</th><th className="num">Status</th><th>Reason</th><th className="num">Latency</th></tr>
          </thead>
          <tbody>
            {result.services.map((s) => (
              <tr key={s.service}>
                <td className="mono">{s.service}</td>
                <td><StatusChip status={s.reachable ? 'ok' : 'unavailable'} /></td>
                <td className="num">{s.status ?? '—'}</td>
                <td className="t-caption">{s.reason ?? '—'}</td>
                <td className="num">{s.latency_ms === null ? '—' : `${s.latency_ms} ms`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

interface ServiceRow {
  service: string;
  url: string;
}

function EnvironmentForm({ environment, onClose }: { environment: EnvironmentView | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const creating = environment === null;
  const [key, setKey] = useState(environment?.key ?? '');
  const [label, setLabel] = useState(environment?.label ?? '');
  const [enabled, setEnabled] = useState(environment?.enabled ?? true);
  // Switching an environment off stops new setups there — confirmed, never a slip of a checkbox.
  const [confirmOff, setConfirmOff] = useState(false);
  const [rows, setRows] = useState<ServiceRow[]>(
    Object.entries(environment?.services ?? {}).map(([service, url]) => ({ service, url })),
  );
  const [internalKey, setInternalKey] = useState('');
  const [postgres, setPostgres] = useState(environment?.postgres_container ?? '');
  const [compose, setCompose] = useState(environment?.compose_project ?? '');
  const [suffix, setSuffix] = useState(environment?.public_host_suffix ?? '');

  const keyError = creating && key && !KEY_PATTERN.test(key) ? 'Lowercase letters, digits, dashes; 2–32; starts with a letter.' : null;
  const rowErrors = rows.map((r) => (r.url ? serviceUrlError(r.url) : 'Required.'));
  const duplicate = new Set(rows.map((r) => r.service)).size !== rows.length;
  const secretError = internalKeyError(internalKey);
  // The stored key is sent to every mapped service: pointing one at a NEW address needs the key re-entered
  // (the server answers 422 KEY_REQUIRED_FOR_NEW_ADDRESS otherwise — a stolen session cannot redirect it).
  const known = new Set(Object.values(environment?.services ?? {}).map(serviceAuthority).filter((a): a is string => a !== null));
  const newAddresses = creating || !environment?.has_internal_api_key
    ? []
    : [...new Set(rows.map((r) => serviceAuthority(r.url)).filter((a): a is string => a !== null && !known.has(a)))];
  const needsKey = newAddresses.length > 0;
  const valid =
    (!creating || KEY_PATTERN.test(key)) && label.trim().length >= 1 && label.trim().length <= 100 &&
    rowErrors.every((e) => e === null) && !duplicate && !secretError && (!needsKey || internalKey !== '');

  const save = useMutation({
    mutationFn: () => {
      const services = Object.fromEntries(rows.map((r) => [r.service, r.url.trim()]));
      if (creating) {
        return opsApi.environments.create({
          key: key.trim(),
          label: label.trim(),
          enabled,
          services,
          internal_api_key: internalKey || undefined,
          postgres_container: postgres.trim() || undefined,
          compose_project: compose.trim() || undefined,
          public_host_suffix: suffix.trim() || undefined,
        });
      }
      // UPDATE: `services` replaces the whole map; an empty string clears a text field.
      return opsApi.environments.update(environment!.key, {
        label: label.trim(),
        enabled,
        services,
        postgres_container: postgres.trim(),
        compose_project: compose.trim(),
        public_host_suffix: suffix.trim(),
        ...(needsKey && internalKey ? { internal_api_key: internalKey } : {}),
      });
    },
    onSuccess: () => {
      setInternalKey('');
      queryClient.invalidateQueries({ queryKey: ['ops', 'environments'] });
      onClose();
    },
  });

  const unused = PLATFORM_SERVICE_KEYS.filter((s) => !rows.some((r) => r.service === s));

  return (
    <Modal
      title={creating ? 'Add environment' : `Edit ${environment!.label}`}
      size="lg"
      onClose={save.isPending ? undefined : onClose}
      dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending}
            onClick={() => (environment?.enabled && !enabled ? setConfirmOff(true) : save.mutate())}>
            {save.isPending ? 'Saving…' : creating ? 'Create environment' : 'Save'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextField label="Key" value={key} onChange={setKey} mono required disabled={!creating} error={keyError}
          hint={creating ? 'Immutable, e.g. tavi, ndc, ashtar-prod.' : 'Immutable.'} />
        <TextField label="Label" value={label} onChange={setLabel} required />
        <TextField label="Postgres container" value={postgres} onChange={setPostgres} mono placeholder="anyware-tavi-postgres-1" />
        <TextField label="Compose project" value={compose} onChange={setCompose} mono placeholder="anyware-tavi"
          hint="Maps collector samples to this environment." />
        <TextField label="Public host suffix" value={suffix} onChange={setSuffix} mono placeholder="portal.example.com" />
      </div>
      <CheckboxField label="Enabled" checked={enabled} onChange={setEnabled}
        hint="A disabled environment refuses new provisioning jobs and every live call." />

      <h3 className="t-heading" style={{ margin: 0 }}>Service URLs</h3>
      <p className="t-caption muted" style={{ margin: 0 }}>
        Internal base URLs reachable from the console's network. Saving replaces the whole map.
      </p>
      {rows.map((row, index) => (
        <div className="row-end" key={index}>
          <div style={{ width: 170 }}>
            <Select
              label="Service"
              value={row.service}
              onChange={(service) => setRows(rows.map((r, i) => (i === index ? { ...r, service } : r)))}
              options={[row.service, ...unused].map((s) => ({ value: s, label: s }))}
            />
          </div>
          <div style={{ flex: '1 1 260px' }}>
            <TextField label="URL" value={row.url} mono
              onChange={(url) => setRows(rows.map((r, i) => (i === index ? { ...r, url } : r)))}
              error={row.url ? rowErrors[index] : null} placeholder="http://billing-api:8080" />
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows(rows.filter((_, i) => i !== index))}>
            Remove
          </button>
        </div>
      ))}
      {duplicate && <span className="t-caption note-error">A service is mapped twice.</span>}
      {unused.length > 0 && (
        <div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows([...rows, { service: unused[0]!, url: '' }])}>
            Add service
          </button>
        </div>
      )}

      {!creating && needsKey && (
        <TextField
          label="Internal API key (required, write-only)"
          type="password"
          mono
          value={internalKey}
          onChange={setInternalKey}
          error={secretError}
          hint={`The key is sent to every service; ${newAddresses.join(', ')} has never received it, so re-enter it to allow that. It is stored again, encrypted.`}
        />
      )}
      {creating && (
        <TextField
          label="Internal API key (optional, write-only)"
          type="password"
          mono
          value={internalKey}
          onChange={setInternalKey}
          error={secretError}
          hint="Encrypted at rest and never shown again. You can also set it later."
        />
      )}
      <MutationError error={save.error} />
      {confirmOff && (
        <ConfirmDialog title={`Switch ${environment?.label ?? key} off?`} confirmLabel="Switch off" destructive pending={save.isPending}
          onConfirm={() => save.mutate()} onCancel={() => setConfirmOff(false)}>
          <p className="t-body" style={{ margin: 0 }}>
            No new tenant can be set up there, and its pages stop reading live figures. Its tenants and servers keep running.
          </p>
        </ConfirmDialog>
      )}
    </Modal>
  );
}

/** `scheme://host:port` of a service URL, the way the server compares addresses (default ports filled in). */
export function serviceAuthority(url: string): string | null {
  try {
    const u = new URL(url.trim());
    const port = u.port || (u.protocol === 'https:' ? '443' : u.protocol === 'http:' ? '80' : '');
    return `${u.protocol.replace(/:$/, '')}://${u.hostname.toLowerCase()}:${port}`;
  } catch {
    return null;
  }
}

function InternalKeyForm({ environment, onClose }: { environment: EnvironmentView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const error = internalKeyError(value);

  const save = useMutation({
    mutationFn: () => opsApi.environments.setKey(environment.key, value),
    onSuccess: () => {
      // The typed key is dropped the moment it has been sent — nothing keeps a copy.
      setValue('');
      queryClient.invalidateQueries({ queryKey: ['ops', 'environments'] });
      onClose();
    },
  });

  return (
    <Modal
      title={`${environment.has_internal_api_key ? 'Rotate' : 'Set'} the internal key — ${environment.label}`}
      onClose={save.isPending ? undefined : onClose}
      dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!value || !!error || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save key'}
          </button>
        </>
      }
    >
      <p className="t-body" style={{ margin: 0 }}>
        The environment's platform <span className="mono">INTERNAL_API_KEY</span>. It is encrypted before it is stored,
        never returned by any read, and never logged — this page will only ever show that a key is set and when.
        {environment.has_internal_api_key && ' Saving replaces the current key immediately.'}
      </p>
      <TextField label="Internal API key" type="password" mono value={value} onChange={setValue} error={error} />
      <MutationError error={save.error} />
    </Modal>
  );
}
